import * as React from 'react';
import styled from 'styled-components';

import Body2 from '@joinforma/ui/components/Body2';
import { Color } from '@joinforma/ui/constants/themes';
import Headline5 from '@joinforma/ui/components/Headline5';
import Headline2 from '@joinforma/ui/components/Headline2';
import Button from '@joinforma/ui/components/Button';
import { ScreenWidth } from '@joinforma/ui/constants/breakpoints';
import {
  ButtonVariant,
  CalloutVariant,
} from '@joinforma/ui/constants/properties';
import Callout from '@joinforma/ui/components/Callout';

import dayjs from 'utils/date-helper';
import { AlegeusRoutes } from 'utils/routeGenerator';

import {
  SUPPORT_HOW_TO_DO_INDIVIDUAL_TRANSFER,
  ALEGEUS_GROUP_ONE_ACCESS_DATE,
  ALEGEUS_GROUP_TWO_ACCESS_DATE,
  SUPPORT_HOW_TO_SELL_ALEGEUS_HSA_INVESTMENTS,
  ALEGEUS_GROUP_TWO_INFO_LINK,
  ALEGEUS_GROUP_ONE_INFO_LINK,
  SUPPORT_HOW_TO_DO_INDIVIDUAL_TRANSFER_V2,
} from 'constants/hsaBulkTransfer';
import { HSA_BULK_TRANSFER_ELEMENTS } from 'constants/testSelectors';

import { StyledAnchor } from './StyledAnchor';

const ConsolidateMessage = styled(Body2)`
  font-weight: 500;
`;

const ConsolidateLink = styled(Body2)<{ onClick: () => void }>`
  color: ${Color.OCEAN_BLUE};
  cursor: pointer;
  margin-left: 4px;
`;

const ConsolidateLinkTwo = styled(Body2)`
  cursor: pointer;
  margin-left: 4px;

  a {
    color: ${({ theme }) => theme.colors.text.link};
  }
`;

/**
 * AL_SM_ST_217_BANNER
 * Member opted in AND liquidated investments
 */
export const AlegeusSmallStoreBanner217V1 = ({
  isMemberAlegeusGroupTwo,
}: {
  isMemberAlegeusGroupTwo: boolean;
}) => (
  <ConsolidateMessage
    data-testid={HSA_BULK_TRANSFER_ELEMENTS.AL_SM_ST_217_BANNER}
  >
    There will be a hold on your 2022 HSA starting{' '}
    {isMemberAlegeusGroupTwo
      ? dayjs(ALEGEUS_GROUP_TWO_ACCESS_DATE).format('MMMM D')
      : dayjs(ALEGEUS_GROUP_ONE_ACCESS_DATE).format('MMMM D')}{' '}
    while we transfer the balance.
    <ConsolidateLinkTwo>
      <StyledAnchor href={SUPPORT_HOW_TO_SELL_ALEGEUS_HSA_INVESTMENTS}>
        Download all documents
      </StyledAnchor>{' '}
      before the hold.
    </ConsolidateLinkTwo>
  </ConsolidateMessage>
);

/**
 * AL_SM_ST_217_V2_BANNER
 * NOT member opted-in && liquidated
 */
export const AlegeusSmallStoreBanner217V2 = () => (
  <ConsolidateMessage
    data-testid={HSA_BULK_TRANSFER_ELEMENTS.AL_SM_ST_217_V2_BANNER}
  >
    We couldn't transfer your HSA because there was an invested balance, but you
    can still transfer it yourself.
    <ConsolidateLinkTwo>
      <StyledAnchor href={SUPPORT_HOW_TO_DO_INDIVIDUAL_TRANSFER_V2}>
        Learn about an individual HSA transfer.
      </StyledAnchor>
    </ConsolidateLinkTwo>
  </ConsolidateMessage>
);

/**
 * NAL_SM_ST_TR_V1_BANNER
 */
export const NonAlegeusSmallStoreTransferBanner = ({
  onClick,
}: {
  onClick: () => void;
}) => (
  <>
    <ConsolidateMessage
      data-testid={HSA_BULK_TRANSFER_ELEMENTS.NAL_SM_ST_TR_V1_BANNER}
    >
      Consolidate all your HSA money and manage it in one place.
      <ConsolidateLink onClick={onClick}>
        Transfer your money to your 2023 Forma HSA.
      </ConsolidateLink>
    </ConsolidateMessage>
  </>
);

/**
 * NAL_SM_ST_AL_NH_CO_V1_BANNER
 */
export const NonAlegeusSmallStoreConfirmBanner = ({
  fundsTransferDate,
}: {
  fundsTransferDate: string;
}) => (
  <ConsolidateMessage
    data-testid={HSA_BULK_TRANSFER_ELEMENTS.NAL_SM_ST_AL_NH_CO_V1_BANNER}
  >
    You opted-in to automatically transfer your old HSA balance to Forma. We'll
    start the transfer after {dayjs(fundsTransferDate).format('LL')}.
  </ConsolidateMessage>
);

/**
 * NAL_SM_AL_NH_MI_V1_BANNER
 * displayed on accounts list & new hsa
 */
export const NonAlegeusSmallAccountsListNewHsaMissedBanner = () => (
  <ConsolidateMessage
    data-testid={HSA_BULK_TRANSFER_ELEMENTS.NAL_SM_AL_NH_MI_V1_BANNER}
  >
    You missed the deadline to transfer your HSA balance automatically, but you
    can still transfer it yourself.
    <ConsolidateLinkTwo>
      <StyledAnchor href={SUPPORT_HOW_TO_DO_INDIVIDUAL_TRANSFER}>
        Learn about individual transfer.
      </StyledAnchor>
    </ConsolidateLinkTwo>
  </ConsolidateMessage>
);

const LargeBannerContainer = styled.div`
  background-color: ${Color.WHITE_100};
  height: 384;
  display: flex;
  align-content: center;
  justify-content: center;
  padding: 20px;

  @media (min-width: ${ScreenWidth.TABLET_PORTRAIT}) {
    height: 304px;
    padding: 20px 16px;
  }

  @media (min-width: ${ScreenWidth.LAPTOP}) {
    height: 304px;
    padding: 50px 0 49px 0;
  }
`;

const XLBannerContainer = styled.div`
  background-color: ${Color.WHITE_100};
  height: 600px;
  display: flex;
  align-content: center;
  justify-content: center;
  padding: 20px;

  @media (min-width: ${ScreenWidth.TABLET_PORTRAIT}) {
    height: 375px;
    padding: 20px 16px;
  }

  @media (min-width: ${ScreenWidth.LAPTOP}) {
    height: 344px;
    padding: 50px 0 49px 0;
  }
`;

// Styles width styles copied from AccountListNew.tsx
const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 343px;

  @media (min-width: ${ScreenWidth.TABLET_PORTRAIT}) {
    max-width: 704px;
  }

  @media (min-width: ${ScreenWidth.LAPTOP}) {
    max-width: 880px;
  }
`;

const Title = styled(Headline2)`
  margin-bottom: 20px;
  font-weight: 500;
`;

const Content = styled(Headline5)`
  margin-bottom: 32px;
`;

const PrimaryButton = styled(Button)``;

const SecondaryButton = styled(Button).attrs({
  variant: ButtonVariant.SECONDARY,
})`
  margin-left: 16px;
  background-color: ${Color.WHITE_100};
  a {
    color: unset;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
`;

/**
 * NAL_LG_AL_TR_V1_BANNER
 */
export const NonAlegeusLargeAccountsListTransferBanner = ({
  onClick,
  optInDeadline,
}: {
  onClick: () => void;
  optInDeadline: string;
}) => (
  <LargeBannerContainer
    data-testid={HSA_BULK_TRANSFER_ELEMENTS.NAL_LG_AL_TR_V1_BANNER}
  >
    <ContentContainer>
      <Title>You can now consolidate your HSA money in one place!</Title>
      <Content>{`We can transfer your balance to your 2023 Forma HSA automatically if you opt-in before ${dayjs(
        optInDeadline
      ).format('LL')}.`}</Content>

      <ButtonContainer>
        <PrimaryButton onClick={onClick}>
          Learn more about HSA Transfer
        </PrimaryButton>
      </ButtonContainer>
    </ContentContainer>
  </LargeBannerContainer>
);

/**
 * NAL_LG_NH_TR_V1_BANNER
 */
export const NonAlegeusLargeNewHsaTransferBanner = ({
  onClick,
  optInDeadline,
}: {
  onClick: () => void;
  optInDeadline: string;
}) => (
  <LargeBannerContainer
    data-testid={HSA_BULK_TRANSFER_ELEMENTS.NAL_LG_NH_TR_V1_BANNER}
  >
    <ContentContainer>
      <Title>You can now consolidate your HSA money in one place!</Title>
      <Content>{`We can transfer your balance to your 2023 Forma HSA automatically if you opt-in before ${dayjs(
        optInDeadline
      ).format('LL')}.`}</Content>

      <ButtonContainer>
        <PrimaryButton onClick={onClick}>
          Learn more about HSA Transfer
        </PrimaryButton>
      </ButtonContainer>
    </ContentContainer>
  </LargeBannerContainer>
);

/**
 *  AL_LG_AL_OH_NH_217_BANNER
 *  Member opted in AND liquidated investments
 */
export const AlegeusLargeAccountsListOldHsaNewHsa217Banner = ({
  isMemberAlegeusGroupTwo,
  alegeusAccountId,
}: {
  isMemberAlegeusGroupTwo: boolean;
  alegeusAccountId: number | null;
}) => {
  const blackoutDate = isMemberAlegeusGroupTwo
    ? dayjs(ALEGEUS_GROUP_TWO_ACCESS_DATE).format('MMMM D')
    : dayjs(ALEGEUS_GROUP_ONE_ACCESS_DATE).format('MMMM D');

  return (
    <LargeBannerContainer
      data-testid={HSA_BULK_TRANSFER_ELEMENTS.AL_LG_AL_OH_NH_217_BANNER}
    >
      <ContentContainer>
        <Title>
          We'll start transferring your HSA balance on {blackoutDate}
        </Title>
        <Content>
          A temporary hold will be put on your 2022 HSA starting {blackoutDate}.
          Download all statements and tax forms from your WealthCare portal
          before the transfer starts.
        </Content>

        <ButtonContainer>
          <PrimaryButton
            onClick={() =>
              window.open(
                alegeusAccountId
                  ? AlegeusRoutes.alegeusHsa({
                      accountId: String(alegeusAccountId),
                    })
                  : AlegeusRoutes.alegeusAccount()
              )
            }
          >
            Log in to my WealthCare Portal
          </PrimaryButton>
          <SecondaryButton
            onClick={
              isMemberAlegeusGroupTwo
                ? () => window.open(ALEGEUS_GROUP_TWO_INFO_LINK)
                : () => window.open(ALEGEUS_GROUP_ONE_INFO_LINK)
            }
          >
            Learn more
          </SecondaryButton>
        </ButtonContainer>
      </ContentContainer>
    </LargeBannerContainer>
  );
};

/**
 *  AL_LG_AL_OH_NH_217_V2_BANNER
 *  NOT member opted-in && liquidated
 */
export const AlegeusLargeAccountsListOldHsaNewHsa217V2Banner = () => (
  <XLBannerContainer
    data-testid={HSA_BULK_TRANSFER_ELEMENTS.AL_LG_AL_OH_NH_217_V2_BANNER}
  >
    <ContentContainer>
      <Title>
        Your HSA won't be transferred to Forma automatically, but you can start
        an individual transfer
      </Title>
      <Content>
        To start an individual HSA transfer you just need to contact WealthCare
        Saver and fill out a transfer form. Otherwise, your HSA will be removed
        from Forma and you'll access it through the WealthCare portal.
      </Content>

      <ButtonContainer>
        <PrimaryButton
          onClick={() => window.open(SUPPORT_HOW_TO_DO_INDIVIDUAL_TRANSFER_V2)}
        >
          Learn more about my HSA options
        </PrimaryButton>
      </ButtonContainer>
    </ContentContainer>
  </XLBannerContainer>
);

const CalloutContainer = styled.div`
  padding: 0 28px 32px 90px;
`;

const CalloutContent = styled(Body2)`
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 18px;
`;

/**
 * AL_CALLOUT_217_V1
 */
export const AlegeusCallout217V1 = () => (
  <CalloutContainer data-testid={HSA_BULK_TRANSFER_ELEMENTS.AL_CALLOUT_217_V1}>
    <Callout
      customContent={
        <CalloutContent>
          We'll start transferring this HSA balance to your new HSA with Forma.
          There will be a hold on this HSA during the transfer, so{' '}
          <StyledAnchor href={SUPPORT_HOW_TO_SELL_ALEGEUS_HSA_INVESTMENTS}>
            download all statements and tax forms
          </StyledAnchor>{' '}
          before the transfer starts.
        </CalloutContent>
      }
      variant={CalloutVariant.WARNING}
    />
  </CalloutContainer>
);
